import { useMemo, useRef, useState } from 'react';

import { Button, Dropdown, Overlay, ThemeProvider } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFileArrowDown } from '@fortawesome/pro-light-svg-icons';

import { BaseRequisition } from '@customTypes/requisition';
import { formatDateForDisplay } from '@helpers/date';
import { getOrderTestName } from '@helpers/patient';
import { getRequisitionReportableOrders, hasRequisitionDownloads } from '@helpers/requisition';
import { useDownload } from '@hooks/useDownload';
import useEffectExceptMount from '@hooks/useEffectExceptMount';

import './RequisitionDownloadMenu.scss';

interface IRequisitionDownloadMenuProps {
  requisition: BaseRequisition;
}

export const RequisitionDownloadMenu: React.FC<IRequisitionDownloadMenuProps> = ({ requisition }) => {
  const { onDownload, onClinicalSummaryLetterDownload } = useDownload();

  const downloadMenuRef = useRef(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const [show, setShow] = useState(false);
  const buttonId = useMemo(() => 'menu_' + requisition.requisition_id, [requisition.requisition_id]);
  const downloadMenuListId = `ul_${buttonId}`;

  const clinicalSummaryCompletedAt = useMemo(
    () => formatDateForDisplay(requisition.clinical_summary_letter?.completed_on),
    [requisition.clinical_summary_letter?.completed_on],
  );

  const orders = useMemo(
    () =>
      getRequisitionReportableOrders(requisition).map(order => ({
        ...order,
        reportTestNames: getOrderTestName(order),
        formattedReportDate: formatDateForDisplay(order.reported_at),
      })),
    [requisition],
  );
  const hasDownloads = hasRequisitionDownloads(requisition);

  useEffectExceptMount(() => {
    if (show) {
      setTimeout(() => firstItemRef.current?.focus(), 100);
    } else {
      // @ts-ignore
      downloadMenuRef.current?.querySelector('button')?.focus();
    }
  }, [show]);

  return (
    <div className='document-download-menu-wrapper' ref={downloadMenuRef}>
      {/* ThemeProvider wipes bs- prefix to customize component without needing to overwrite Bootstrap styles */}
      <ThemeProvider prefixes={{ btn: 'ag-btn' }}>
        {hasDownloads && (
          <Button
            id={buttonId}
            className={show ? 'document-download-menu-toggle show' : 'document-download-menu-toggle'}
            onClick={() => setShow(true)}
            aria-label={`Download menu for order ${requisition.requisition_id}`}
            aria-controls={downloadMenuListId}
            aria-haspopup='true'
          >
            <FontAwesomeIcon icon={faFileArrowDown as IconDefinition} fontSize={16} />
          </Button>
        )}
        {!hasDownloads && <span>&mdash;</span>}
      </ThemeProvider>
      <Overlay
        target={downloadMenuRef.current}
        show={show}
        placement='right-start'
        onHide={() => setShow(false)}
        rootClose
      >
        <ul
          id={downloadMenuListId}
          className='document-download-menu dropdown-menu'
          aria-labelledby={buttonId}
          tabIndex={0}
        >
          {orders.length > 0 && (
            <li>
              <Dropdown.ItemText as='h5' className='mb-0'>
                <div className='bg-body-tertiary width-100 text-center text-body-secondary fs-xs'>Reports</div>
              </Dropdown.ItemText>
            </li>
          )}
          {orders.map((order, index) => (
            <li key={'li_report_' + index} className='document-download-li'>
              <Dropdown.Item
                as='button'
                ref={index === 0 ? firstItemRef : null}
                onClick={() => {
                  onDownload(order, requisition.requisition_id);
                  setShow(false);
                }}
                aria-label='Download'
              >
                {!!order.reportTestNames.brand && (
                  <span className='d-block fs-xs fw-medium text-secondary'>{order.reportTestNames.brand}</span>
                )}
                {!!order.reportTestNames.productLine && (
                  <span className='d-block fs-sm lh-sm'>{order.reportTestNames.productLine}</span>
                )}
                {!!order.reportTestNames.productName && (
                  <span className='d-block fs-xs'>{order.reportTestNames.productName}</span>
                )}
                <time dateTime={order.reported_at ?? undefined} className='d-block fs-xs text-secondary mt-1'>
                  {order.formattedReportDate}
                </time>
              </Dropdown.Item>
            </li>
          ))}
          {!!requisition.clinical_summary_letter && (
            <>
              <li>
                <Dropdown.ItemText as='h5' className='mb-0'>
                  <div className='bg-body-tertiary width-100 text-center text-body-secondary fs-xs'>
                    Other Documents
                  </div>
                </Dropdown.ItemText>
              </li>
              <li className='document-download-li'>
                <Dropdown.Item
                  as='button'
                  onClick={() => {
                    onClinicalSummaryLetterDownload(requisition);
                    setShow(false);
                  }}
                  aria-label='Download'
                >
                  <span className='d-block fs-sm lh-sm'>Clinical Summary Letter</span>
                  <time
                    dateTime={requisition.clinical_summary_letter.completed_on}
                    className='d-block fs-xs text-secondary mt-1'
                  >
                    {clinicalSummaryCompletedAt}
                  </time>
                </Dropdown.Item>
              </li>
            </>
          )}
        </ul>
      </Overlay>
    </div>
  );
};
